// Team — Mitarbeiter anlegen, Rolle ändern, Passwort zurücksetzen.
//
// Der Chef legt das Konto an und gibt dem Mitarbeiter das Startpasswort.
// Ein Bestätigungsmail gibt es bewusst nicht — der Hof hat keine Mailadressen
// für alle, und der Chef sitzt ohnehin daneben.

import { useMemo, useState } from "react";
import { useTeam } from "@/lib/qs";
import {
  useAlleZeiten,
  useZeitenVon,
  dauerMinuten,
  formatDauer,
  type TimeEntry,
} from "@/lib/zeit";
import {
  useMitarbeiterAnlegen,
  useMitarbeiterAendern,
  useMitarbeiterLoeschen,
  useNaechstePersonalnummer,
  passwortVorschlag,
  ROLLEN,
  type Rolle,
} from "@/lib/team";
import { useAuth } from "@/stores/authStore";
import { RECHTE } from "@/lib/rechte";
import { ZUTEILBARE_MODULE } from "@/lib/module-registry";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Field, Input, Sheet, cn } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

interface Mitarbeiter {
  id: string;
  full_name: string | null;
  role: string;
  allowed_modules: string[] | null;
  permissions: string[] | null;
  personalnummer: string | null;
  urlaubstage_jahr: number;
  /** Steht auf der Lohnliste des Hofs. false = Zugang ohne Anstellung. */
  im_lohn: boolean;
}

export default function AdminTeamPage() {
  const { data: team = [], isLoading } = useTeam();
  const [offen, setOffen] = useState<Mitarbeiter | "neu" | null>(null);

  const wocheVon = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Montag
    return d.toISOString();
  }, []);
  const { data: zeiten = [] } = useAlleZeiten(wocheVon);

  // Kundenkonten gehören nicht ins Team.
  const mitarbeiter = team.filter((m) => !m.role.startsWith("kunde"));

  const proPerson = useMemo(() => {
    const map = new Map<string, { summe: number; aktiv: boolean }>();
    for (const z of zeiten) {
      const g = map.get(z.profile_id) ?? { summe: 0, aktiv: false };
      if (z.ended_at) g.summe += dauerMinuten(z);
      else g.aktiv = true;
      map.set(z.profile_id, g);
    }
    return map;
  }, [zeiten]);

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader
        titel="Team"
        untertitel={isLoading ? undefined : `${mitarbeiter.length} Mitarbeiter`}
        aktion={
          <Button variant="primary" size="sm" onClick={() => setOffen("neu")}>
            + Mitarbeiter
          </Button>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LogoLoader size="md" label="Team wird geladen" />
          </div>
        ) : mitarbeiter.length === 0 ? (
          <EmptyState
            title="Noch kein Team"
            hint="Lege das erste Mitarbeiterkonto an."
            action={
              <Button variant="primary" onClick={() => setOffen("neu")}>
                Mitarbeiter anlegen
              </Button>
            }
          />
        ) : (
          <>
            <p className="mb-3 text-sm text-ink-400">
              Gearbeitete Stunden dieser Woche (ab Montag, ohne Pausen) — nur bei
              Angestellten auf der Lohnliste. Antippen zum Bearbeiten.
            </p>
            <ul className="space-y-2">
              {mitarbeiter.map((m) => {
                const z = proPerson.get(m.id);
                return (
                  <li key={m.id}>
                    <button onClick={() => setOffen(m)} className="w-full text-left">
                      <Card className="flex items-center justify-between gap-3 px-4 py-3.5 transition-colors duration-fast hover:border-ink-600">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[15px] font-medium text-ink-100">
                              {m.full_name ?? "Ohne Namen"}
                            </span>
                            {z?.aktiv && (
                              <span className="flex shrink-0 items-center gap-1 rounded bg-gruen-500/15 px-1.5 py-0.5 text-xs font-medium text-gruen-400">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gruen-400" />
                                aktiv
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-sm text-ink-400">
                            <span>{rollenName(m.role)}</span>
                            {/* Wer keine Personalnummer hat, taucht im Lohn-Export
                                als Namenloser auf. Das muss man SEHEN, ohne jeden
                                Mitarbeiter einzeln zu öffnen. */}
                            {m.personalnummer ? (
                              <span className="font-mono text-xs text-ink-500">
                                {m.personalnummer}
                              </span>
                            ) : m.im_lohn ? (
                              // Fehlt bei jemandem, der auf der Lohnliste steht:
                              // Der taucht im Export als Namenloser auf.
                              <span className="rounded bg-warn-bg/15 px-1.5 py-0.5 text-xs font-medium text-warn-fg">
                                keine Personalnr.
                              </span>
                            ) : (
                              // Entwickler, Testzugänge: Das ist kein Versäumnis.
                              <span className="text-xs text-ink-600">nicht im Lohn</span>
                            )}
                          </div>
                        </div>

                        {/* Stunden nur bei Leuten, die auch stempeln.
                            Sebastian ist Geschäftsführer, Marvin und Schmelz sind
                            Entwickler — bei ihnen stünde hier für immer "0:00 h".
                            Eine Null, die nie etwas anderes wird, liest man nach
                            drei Tagen nicht mehr, und dann übersieht man auch die
                            Null bei jemandem, der eigentlich gearbeitet haben
                            müsste. Massgeblich ist die Lohnliste, nicht die
                            Rolle: Wer im Lohn steht, dessen Stunden zählen. */}
                        {m.im_lohn && (
                          <div
                            className={cn(
                              "shrink-0 text-[15px] font-semibold tabular-nums",
                              z?.summe ? "text-ink-50" : "text-ink-500"
                            )}
                          >
                            {formatDauer(z?.summe ?? 0)}
                          </div>
                        )}
                      </Card>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {offen && (
        <MitarbeiterSheet
          m={offen === "neu" ? null : offen}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  );
}

function MitarbeiterSheet({ m, onClose }: { m: Mitarbeiter | null; onClose: () => void }) {
  const anlegen = useMitarbeiterAnlegen();
  const aendern = useMitarbeiterAendern();
  const loeschen = useMitarbeiterLoeschen();
  const ichSelbst = useAuth((s) => s.session?.user.id);

  const neu = !m;
  const [name, setName] = useState(m?.full_name ?? "");
  const [email, setEmail] = useState("");
  const [rolle, setRolle] = useState<Rolle>((m?.role as Rolle) ?? "fahrer");
  const [passwort, setPasswort] = useState(neu ? passwortVorschlag() : "");
  const [loeschFrage, setLoeschFrage] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<{ email: string; passwort: string } | null>(null);

  // Für den Lohn: ohne Personalnummer kann der Steuerberater die Stunden
  // niemandem zuordnen. Beim Anlegen schlägt der Server die nächste freie vor —
  // der Chef soll nicht selbst nachsehen müssen, welche noch frei ist.
  const { data: vorschlag } = useNaechstePersonalnummer(!m);
  const [personalnr, setPersonalnr] = useState(m?.personalnummer ?? "");
  const [urlaubstage, setUrlaubstage] = useState(String(m?.urlaubstage_jahr ?? 25));

  // Nicht jeder mit einem Zugang ist angestellt: Die Entwickler arbeiten an der
  // App, sie stehen nicht auf der Lohnliste des Hofs. Ihnen bloß die
  // Personalnummer wegzunehmen reichte nicht — dann stünde bei ihnen "keine
  // Personalnr.", als hätte man etwas vergessen. Eine Warnung, die man ignorieren
  // MUSS, wird irgendwann überall ignoriert.
  const [imLohn, setImLohn] = useState(m?.im_lohn ?? true);

  // Den Vorschlag nur EINMAL übernehmen — sonst überschriebe er, was der Chef
  // gerade tippt, sobald die Abfrage nachlädt.
  const [vorschlagGesetzt, setVorschlagGesetzt] = useState(false);
  if (!m && vorschlag && !personalnr && !vorschlagGesetzt) {
    setPersonalnr(vorschlag);
    setVorschlagGesetzt(true);
  }

  // Rechte: was die Person TUN darf. Wird in der Datenbank durchgesetzt.
  const [rechte, setRechte] = useState<string[]>(m?.permissions ?? []);

  // Module: was sie im Menü SIEHT. NULL = alles außer Verwaltung. Für die
  // Bedienung wird daraus ein "alle"-Schalter plus eine Auswahl — sonst müsste
  // man den Unterschied zwischen "kein Eintrag" und "alle einzeln angehakt"
  // erklären, und den versteht niemand.
  const [alleModule, setAlleModule] = useState(!m?.allowed_modules);
  const [module, setModule] = useState<string[]>(
    m?.allowed_modules ?? ZUTEILBARE_MODULE.map((x) => x.id)
  );

  const binIchSelbst = m?.id === ichSelbst;
  const istAdmin = rolle === "admin";

  const kannSpeichern = neu
    ? name.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && passwort.length >= 8
    : name.trim().length > 1;

  const toggle = (liste: string[], setzen: (v: string[]) => void, id: string) =>
    setzen(liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id]);

  const speichern = async () => {
    setFehler(null);
    try {
      if (neu) {
        await anlegen.mutateAsync({
          email: email.trim(),
          password: passwort,
          full_name: name.trim(),
          role: rolle,
          im_lohn: imLohn,
          personalnummer: imLohn ? personalnr.trim() || undefined : undefined,
          urlaubstage_jahr: Number(urlaubstage.replace(",", ".")) || 25,
        });
        // Zugangsdaten NICHT einfach wegblenden — der Chef muss sie weitergeben.
        setFertig({ email: email.trim(), passwort });
      } else {
        await aendern.mutateAsync({
          id: m.id,
          full_name: name.trim(),
          role: rolle,
          ...(passwort ? { password: passwort } : {}),
          permissions: rechte,
          // "Profil" darf nie fehlen — sonst käme die Person nie an ihr eigenes
          // Passwort und könnte sich nicht abmelden.
          allowed_modules: alleModule ? null : [...new Set([...module, "profil"])],
          im_lohn: imLohn,
          personalnummer: imLohn ? personalnr.trim() || null : null,
          urlaubstage_jahr: Number(urlaubstage.replace(",", ".")) || 25,
        });
        onClose();
      }
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  // Nach dem Anlegen: Zugangsdaten zeigen, damit der Chef sie abschreiben kann.
  if (fertig) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="Konto angelegt"
        footer={
          <Button variant="primary" size="lg" className="w-full" onClick={onClose}>
            Fertig
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="text-[15px] text-ink-200">
            Gib {name} diese Zugangsdaten. Beim ersten Anmelden sollte er das Passwort ändern.
          </p>

          <div className="space-y-2 rounded-xl border border-gruen-500/30 bg-gruen-500/10 p-4">
            <Zeile label="E-Mail" wert={fertig.email} />
            <Zeile label="Passwort" wert={fertig.passwort} />
          </div>

          <p className="text-sm text-ink-500">
            Das Passwort steht nur jetzt hier — später lässt es sich nur noch neu setzen,
            nicht auslesen.
          </p>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={neu ? "Neuer Mitarbeiter" : (m.full_name ?? "Mitarbeiter")}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!kannSpeichern}
            loading={anlegen.isPending || aendern.isPending}
            onClick={speichern}
          >
            {neu ? "Konto anlegen" : "Speichern"}
          </Button>

          {!neu &&
            !binIchSelbst &&
            (loeschFrage ? (
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setLoeschFrage(false)}>
                  Abbrechen
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  loading={loeschen.isPending}
                  onClick={async () => {
                    setFehler(null);
                    try {
                      await loeschen.mutateAsync(m.id);
                      onClose();
                    } catch (e) {
                      setFehler((e as Error).message);
                      setLoeschFrage(false);
                    }
                  }}
                >
                  Wirklich löschen
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setLoeschFrage(true)}
              >
                Konto löschen
              </Button>
            ))}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Arbeitszeiten zuerst, noch vor dem Namen: Wer einen Mitarbeiter
            öffnet, will meistens wissen, ob er da ist und wie viel er diese
            Woche gearbeitet hat — nicht seinen Namen ändern. */}
        {!neu && m && <ArbeitszeitenBlock profileId={m.id} />}

        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Max Mustermann"
            autoFocus={neu}
          />
        </Field>

        {neu && (
          <Field label="E-Mail" hint="Damit meldet er sich an">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="max@hof-sirges.de"
              autoCapitalize="off"
              inputMode="email"
            />
          </Field>
        )}

        <div>
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-400">
            Rolle
          </span>
          <div className="space-y-1.5">
            {ROLLEN.map((r) => (
              <button
                key={r.wert}
                onClick={() => setRolle(r.wert)}
                disabled={binIchSelbst && r.wert !== "admin"}
                className={cn(
                  "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left",
                  "transition-colors duration-fast",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
                  "disabled:cursor-not-allowed disabled:opacity-40",
                  rolle === r.wert
                    ? "border-gruen-500/50 bg-gruen-500/10"
                    : "border-ink-700 bg-ink-900 hover:border-ink-600"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2",
                    rolle === r.wert ? "border-gruen-500" : "border-ink-600"
                  )}
                >
                  {rolle === r.wert && <span className="h-2.5 w-2.5 rounded-full bg-gruen-500" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium text-ink-100">{r.label}</span>
                  <span className="mt-0.5 block text-sm text-ink-400">{r.hint}</span>
                </span>
              </button>
            ))}
          </div>

          {binIchSelbst && (
            <p className="mt-2 text-sm text-ink-500">
              Das bist du selbst — die eigenen Admin-Rechte lassen sich nicht entziehen.
            </p>
          )}
        </div>

        {/* ── Lohn */}
        <div>
          <Haken
            an={imLohn}
            onClick={() => setImLohn((v) => !v)}
            titel="Steht auf der Lohnliste"
            hinweis="Angestellte des Hofs. Entwickler und Testzugänge nicht."
          />

          {/* Personalnummer nur, wenn es sie überhaupt braucht. Ein Feld, das man
              leer lassen soll, ist ein Feld, das man falsch ausfüllt. */}
          {imLohn ? (
            <div className="mt-3 flex gap-3">
              <Field
                label="Personalnummer"
                hint={
                  neu ? "Vorschlag — kann überschrieben werden" : "Muss zur Nummer in DATEV passen"
                }
              >
                <Input
                  value={personalnr}
                  onChange={(e) => setPersonalnr(e.target.value)}
                  placeholder={vorschlag ?? "1001"}
                  className="font-mono"
                />
              </Field>
              <Field label="Urlaubstage" hint="pro Jahr">
                <Input
                  value={urlaubstage}
                  onChange={(e) => setUrlaubstage(e.target.value)}
                  inputMode="decimal"
                  className="tabular-nums"
                />
              </Field>
            </div>
          ) : (
            <p className="mt-2 text-sm text-ink-500">
              Keine Personalnummer, taucht nicht im Lohn-Export auf. Die Stempeluhr
              funktioniert trotzdem.
            </p>
          )}
        </div>

        {/* ── Rechte und Module: erst nach dem Anlegen. Beim neuen Konto wären
            es zwei Entscheidungen zu viel auf einmal; die Voreinstellung
            (Standard-Module, keine Sonderrechte) passt fast immer. */}
        {!neu && !istAdmin && (
          <>
            <div>
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-400">
                Darf zusätzlich
              </span>
              <div className="space-y-1.5">
                {RECHTE.map((r) => (
                  <Haken
                    key={r.id}
                    an={rechte.includes(r.id)}
                    onClick={() => toggle(rechte, setRechte, r.id)}
                    titel={r.label}
                  />
                ))}
              </div>
              <p className="mt-2 text-sm text-ink-500">
                Ohne Haken kann er Produkte nur ansehen.
              </p>
            </div>

            <div>
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-ink-400">
                Sichtbare Module
              </span>

              <Haken
                an={alleModule}
                onClick={() => setAlleModule((v) => !v)}
                titel="Alle Module"
                hinweis="Standard — er sieht alles außer der Verwaltung"
              />

              {!alleModule && (
                <div className="mt-1.5 space-y-1.5 border-l-2 border-ink-700 pl-3">
                  {ZUTEILBARE_MODULE.filter((x) => x.id !== "profil").map((x) => (
                    <Haken
                      key={x.id}
                      an={module.includes(x.id)}
                      onClick={() => toggle(module, setModule, x.id)}
                      titel={x.label}
                      hinweis={x.hinweis}
                    />
                  ))}
                  <p className="pt-1 text-sm text-ink-500">
                    Das Profil bleibt immer sichtbar — sonst käme er nie an sein eigenes
                    Passwort.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {!neu && istAdmin && (
          <p className="rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5 text-sm text-ink-400">
            Admins sehen alle Module und dürfen alles. Es gibt nichts zu haken.
          </p>
        )}

        <Field
          label={neu ? "Startpasswort" : "Neues Passwort"}
          optional={!neu}
          hint={
            neu
              ? "Gib es dem Mitarbeiter weiter — mindestens 8 Zeichen"
              : "Leer lassen, wenn es bleiben soll"
          }
        >
          <div className="flex gap-2">
            <Input
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              placeholder={neu ? "" : "unverändert"}
              className="flex-1 font-mono"
              autoCapitalize="off"
            />
            <Button className="shrink-0" onClick={() => setPasswort(passwortVorschlag())}>
              Würfeln
            </Button>
          </div>
        </Field>
      </div>
    </Sheet>
  );
}

function Haken({
  an,
  onClick,
  titel,
  hinweis,
}: {
  an: boolean;
  onClick: () => void;
  titel: string;
  hinweis?: string;
}) {
  return (
    <button
      onClick={onClick}
      role="checkbox"
      aria-checked={an}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left",
        "transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
        an ? "border-gruen-500/50 bg-gruen-500/10" : "border-ink-700 bg-ink-900 hover:border-ink-600"
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded border-2 text-xs font-bold",
          an ? "border-gruen-500 bg-gruen-500 text-gruen-on" : "border-ink-600"
        )}
      >
        {an && "✓"}
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-medium text-ink-100">{titel}</span>
        {hinweis && <span className="mt-0.5 block text-sm text-ink-400">{hinweis}</span>}
      </span>
    </button>
  );
}

function Zeile({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-sm text-ink-400">{label}</span>
      <span className="select-all font-mono text-[15px] font-medium text-ink-50">{wert}</span>
    </div>
  );
}

function rollenName(r: string) {
  return ROLLEN.find((x) => x.wert === r)?.label ?? r;
}

// ─────────────────────────────────────────────── Arbeitszeiten im Profil

/**
 * Die Zeiten EINES Mitarbeiters, dort wo Sebastian sie sucht: in seinem Profil.
 *
 * Ganz oben die einzige Frage, die zwischendurch wirklich zählt — ist er gerade
 * eingestempelt? Danach die Summen dieser und letzter Woche, dann die einzelnen
 * Tage.
 *
 * Ohne Eintrag steht hier ausdrücklich "noch nie eingestempelt" und nicht bloß
 * eine leere Fläche: Genau daran hing die Fehlersuche — eine leere Liste kann
 * heißen "hat nicht gearbeitet" ODER "es kommt nichts an", und das darf man
 * nicht verwechseln.
 */
function ArbeitszeitenBlock({ profileId }: { profileId: string }) {
  const { data: zeiten = [], isLoading } = useZeitenVon(profileId);

  const laufend = zeiten.find((z) => !z.ended_at && !("storniert_am" in z && z.storniert_am));

  const montagVor = (wochenZurueck: number) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - wochenZurueck * 7);
    return d;
  };
  const summe = (von: Date, bis: Date) =>
    zeiten
      .filter((z) => z.ended_at && new Date(z.started_at) >= von && new Date(z.started_at) < bis)
      .reduce((s, z) => s + dauerMinuten(z), 0);

  const dieseWoche = summe(montagVor(0), new Date(Date.now() + 864e5));
  const letzteWoche = summe(montagVor(1), montagVor(0));

  return (
    <section className="rounded-xl border border-ink-700 bg-ink-900/60 p-4">
      <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-ink-400">
        Arbeitszeiten
      </h3>

      {isLoading ? (
        <p className="py-2 text-sm text-ink-500">lädt …</p>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <span
              className={cn(
                "h-2 w-2 shrink-0 rounded-full",
                laufend ? "bg-gruen-500" : "bg-ink-600"
              )}
              aria-hidden
            />
            <span className={cn("text-[15px]", laufend ? "text-gruen-400" : "text-ink-400")}>
              {laufend
                ? `eingestempelt seit ${new Date(laufend.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`
                : "nicht eingestempelt"}
            </span>
          </div>

          <div className="mb-3 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-ink-800/60 px-3 py-2">
              <div className="text-xs text-ink-500">Diese Woche</div>
              <div className="text-lg font-semibold tabular-nums text-ink-50">
                {formatDauer(dieseWoche)}
              </div>
            </div>
            <div className="rounded-lg bg-ink-800/60 px-3 py-2">
              <div className="text-xs text-ink-500">Letzte Woche</div>
              <div className="text-lg font-semibold tabular-nums text-ink-50">
                {formatDauer(letzteWoche)}
              </div>
            </div>
          </div>

          {zeiten.length === 0 ? (
            <p className="py-2 text-sm text-ink-500">
              Noch nie eingestempelt — es liegt kein einziger Eintrag vor.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {zeiten.slice(0, 10).map((z) => (
                <ZeitReihe key={z.id} z={z} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function ZeitReihe({ z }: { z: TimeEntry & { storniert_am?: string | null } }) {
  const start = new Date(z.started_at);
  const uhr = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const storniert = !!z.storniert_am;

  return (
    <li
      className={cn(
        "flex items-baseline justify-between gap-3 border-b border-ink-800 pb-1.5 last:border-0",
        storniert && "opacity-50"
      )}
    >
      <span className="text-sm text-ink-300">
        {start.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
      </span>
      <span className={cn("text-sm tabular-nums text-ink-400", storniert && "line-through")}>
        {uhr(start)} – {z.ended_at ? uhr(new Date(z.ended_at)) : "läuft"}
        {z.break_minutes > 0 && ` · ${z.break_minutes} min Pause`}
      </span>
      <span className={cn("text-sm font-medium tabular-nums text-ink-100", storniert && "line-through")}>
        {z.ended_at ? formatDauer(dauerMinuten(z)) : "—"}
      </span>
    </li>
  );
}
